import { useFormikContext } from 'formik';
import { ReactNode } from 'react';
import { GitCommitIcon, LayersIcon, LayoutGridIcon } from 'lucide-react';

import { addPlural } from '@/react/common/string-utils';

import { StickyFooter } from '@@/StickyFooter/StickyFooter';
import { Button, LoadingButton } from '@@/buttons';
import { Icon, IconType } from '@@/Icon';

import { FormValues } from './types';
import { useWizardContext } from './WizardContext';

type Props = {
  /** Label of the submit button on the last step. */
  submitLabel?: string;
  /** Label of the submit button while the last step is submitting. */
  submitLoadingLabel?: string;
};

export function WizardFooter({
  submitLabel = 'Create',
  submitLoadingLabel = 'Creating...',
}: Props) {
  const { isFirstStep, isLastStep, goToPreviousStep, currentStep } =
    useWizardContext();
  const { isSubmitting, isValid, values } = useFormikContext<FormValues>();

  const targetGroupCount = new Set(
    values.artifacts.flatMap((artifact) => artifact.targets)
  ).size;

  // Step ids: 'source', 'artifacts', one 'config-<index>' per artifact, 'review'.
  const pastSourceStep = currentStep.id !== 'source';
  const pastArtifactsStep = pastSourceStep && currentStep.id !== 'artifacts';
  const onReviewStep = currentStep.id === 'review';

  return (
    <StickyFooter>
      <div className="flex gap-4">
        {pastSourceStep && values.sources.length > 0 && (
          <Stat label="Sources" icon={GitCommitIcon}>
            {addPlural(values.sources.length, 'source')}
          </Stat>
        )}
        {pastArtifactsStep && values.artifacts.length > 0 && (
          <Stat label="Artifacts" icon={LayersIcon}>
            {addPlural(values.artifacts.length, 'artifact')}
          </Stat>
        )}
        {onReviewStep && values.artifacts.length > 0 && (
          <Stat label="Targets" icon={LayoutGridIcon}>
            {addPlural(targetGroupCount, 'group')}
          </Stat>
        )}
      </div>

      <div className="ml-auto flex justify-end gap-4">
        <Button
          color="default"
          type="button"
          onClick={goToPreviousStep}
          disabled={isFirstStep}
          data-cy="gitops-source-wizard-back-button"
          size="medium"
        >
          Back
        </Button>
        <LoadingButton
          color="primary"
          type="submit"
          disabled={!isValid}
          data-cy="gitops-source-wizard-continue-button"
          size="medium"
          isLoading={isSubmitting}
          loadingText={isLastStep ? submitLoadingLabel : ''}
        >
          {isLastStep ? submitLabel : 'Continue'}
        </LoadingButton>
      </div>
    </StickyFooter>
  );
}

function Stat({
  icon,
  label,
  children,
}: {
  icon: IconType;
  label: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-1 text-xs font-bold tracking-wider text-gray-6 uppercase th-highcontrast:text-white th-dark:text-gray-2">
        <Icon icon={icon} /> {label}
      </div>
      <div className="font-semibold tracking-wider text-gray-10 th-highcontrast:text-white th-dark:text-gray-2">
        {children}
      </div>
    </div>
  );
}
